import { c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore, useRef } from "react";
import { isSimulatedResponse, isPending, isError, isSuccess } from "../guards.js";
import { QUERY_KEYS } from "../constants/keys.js";
const queryKeysMatch = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const useCacheKeyVersion = prefix => {
  const $ = c(4);
  const queryClient = useQueryClient();
  const versionRef = useRef(0);
  let t0;
  if ($[0] !== prefix || $[1] !== queryClient) {
    t0 = onStoreChange => {
      let scheduled = false;
      return queryClient.getQueryCache().subscribe(event => {
        if (event.query?.queryKey?.[0] !== prefix) {
          return;
        }
        if (scheduled) {
          return;
        }
        scheduled = true;
        queueMicrotask(() => {
          scheduled = false;
          versionRef.current = versionRef.current + 1;
          onStoreChange();
        });
      });
    };
    $[0] = prefix;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const subscribe = t0;
  let t1;
  if ($[3] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => versionRef.current;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getSnapshot = t1;
  return useSyncExternalStore(subscribe, getSnapshot, _temp);
};
const useCacheDataVersion = queryKey => {
  const $ = c(4);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== queryClient || $[1] !== queryKey) {
    t0 = onStoreChange => {
      let scheduled = false;
      return queryClient.getQueryCache().subscribe(event => {
        const eventQueryKey = event.query?.queryKey;
        if (!eventQueryKey || !queryKeysMatch(eventQueryKey, queryKey)) {
          return;
        }
        if (scheduled) {
          return;
        }
        scheduled = true;
        queueMicrotask(() => {
          scheduled = false;
          onStoreChange();
        });
      });
    };
    t1 = () => JSON.stringify(queryClient.getQueryData(queryKey) ?? null);
    $[0] = queryClient;
    $[1] = queryKey;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  return useSyncExternalStore(t0, t1, _temp2);
};
const toCachedData = cachedData => {
  if (!cachedData) {
    return {
      data: null,
      isPending: true
    };
  }
  if (isSimulatedResponse(cachedData)) {
    if (isPending(cachedData)) {
      return {
        data: null,
        isPending: true
      };
    }
    if (isError(cachedData)) {
      return {
        data: null,
        isError: true,
        errorMessage: cachedData.errorMessage
      };
    }
    if (isSuccess(cachedData)) {
      return {
        data: cachedData.data,
        isPending: false
      };
    }
  }
  return {
    data: cachedData,
    isPending: false
  };
};
const GOALS_KEY = [QUERY_KEYS.GOALS];
const VARIABLES_KEY = [QUERY_KEYS.VARIABLES];
const useCacheValue = queryKey => {
  const $ = c(6);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient || $[1] !== queryKey) {
    t0 = onStoreChange => {
      let scheduled = false;
      return queryClient.getQueryCache().subscribe(event => {
        const eventQueryKey = event.query?.queryKey;
        if (!eventQueryKey || !queryKeysMatch(eventQueryKey, queryKey)) {
          return;
        }
        if (scheduled) {
          return;
        }
        scheduled = true;
        queueMicrotask(() => {
          scheduled = false;
          onStoreChange();
        });
      });
    };
    $[0] = queryClient;
    $[1] = queryKey;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const subscribe = t0;
  let t1;
  if ($[3] !== queryClient || $[4] !== queryKey) {
    t1 = () => queryClient.getQueryData(queryKey);
    $[3] = queryClient;
    $[4] = queryKey;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const getSnapshot = t1;
  return useSyncExternalStore(subscribe, getSnapshot, _temp3);
};
const useCache = () => {
  const $ = c(78);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t0 = [QUERY_KEYS.GOALS];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const goalsVersion = useCacheDataVersion(t0);
  let t1;
  if ($[1] === /* @__PURE__ */Symbol.for("react.memo_cache_sentinel")) {
    t1 = [QUERY_KEYS.VARIABLES];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const variablesVersion = useCacheDataVersion(t1);
  const dataVersion = useCacheKeyVersion(QUERY_KEYS.DATA_KEY);
  const goalsRaw = useCacheValue(GOALS_KEY);
  const variablesRaw = useCacheValue(VARIABLES_KEY);
  let t2;
  if ($[2] !== goalsRaw) {
    t2 = toCachedData(goalsRaw);
    $[2] = goalsRaw;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const goals = t2;
  let t3;
  if ($[4] !== variablesRaw) {
    t3 = toCachedData(variablesRaw);
    $[4] = variablesRaw;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const variables = t3;
  let t4;
  if ($[6] !== queryClient) {
    t4 = function (queryKey) {
      return toCachedData(queryClient.getQueryData(queryKey));
    };
    $[6] = queryClient;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const getCachedData = t4;
  let t5;
  if ($[8] !== queryClient) {
    t5 = (queryKey_0, data, t62) => {
      const safeUpdate = t62 === void 0 ? false : t62;
      if (!queryKey_0 || !Array.isArray(queryKey_0) || queryKey_0.length === 0) {
        return;
      }
      if (safeUpdate) {
        const cachedData = queryClient.getQueryData(queryKey_0);
        if (cachedData && JSON.stringify(cachedData) === JSON.stringify(data)) {
          console.warn(`Data for key "${queryKey_0.join(":")}" is already defined, safe mode is on.`);
          return;
        }
      }
      queryClient.setQueryData(queryKey_0, data);
    };
    $[8] = queryClient;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const setCacheData = t5;
  let t6;
  if ($[10] !== queryClient) {
    t6 = (queryKey_1, data_0) => {
      if (!queryKey_1 || !data_0 || Object.keys(data_0).length === 0) {
        return;
      }
      const existingData = queryClient.getQueryData(queryKey_1) || {};
      const updatedData = {
        ...existingData
      };
      Object.entries(data_0).forEach(t72 => {
        const [key, value] = t72;
        if (value !== null || existingData[key] === void 0 || existingData[key] === null) {
          updatedData[key] = value;
        }
      });
      queryClient.setQueryData(queryKey_1, updatedData);
    };
    $[10] = queryClient;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const updateCacheData = t6;
  let t7;
  if ($[12] !== queryClient) {
    t7 = async queryKey_2 => {
      if (!queryKey_2 || !Array.isArray(queryKey_2) || queryKey_2.length === 0) {
        return;
      }
      await queryClient.resetQueries({
        queryKey: queryKey_2
      });
    };
    $[12] = queryClient;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const resetCacheData = t7;
  let t8;
  if ($[14] !== queryClient) {
    t8 = (key_0, status, t92, t102) => {
      const data_1 = t92 === void 0 ? null : t92;
      const errorMessage = t102 === void 0 ? null : t102;
      const simulatedData = {
        __status: status,
        ...(data_1 ? {
          data: data_1
        } : {}),
        ...(status === "error" && errorMessage ? {
          errorMessage
        } : {})
      };
      queryClient.setQueryData([QUERY_KEYS.DATA_KEY, key_0], simulatedData);
    };
    $[14] = queryClient;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const addSimulatedData = t8;
  let t9;
  if ($[16] !== getCachedData) {
    t9 = dataKey => getCachedData([QUERY_KEYS.DATA_KEY, dataKey]);
    $[16] = getCachedData;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const getData = t9;
  let t10;
  if ($[18] !== getCachedData) {
    t10 = dataKey_0 => getCachedData([QUERY_KEYS.DATA_SOURCE, dataKey_0]);
    $[18] = getCachedData;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const getDataSource = t10;
  let t11;
  if ($[20] !== getCachedData) {
    t11 = () => getCachedData([QUERY_KEYS.GOALS]);
    $[20] = getCachedData;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const getGoals = t11;
  let t12;
  if ($[22] !== getCachedData) {
    t12 = () => getCachedData([QUERY_KEYS.VARIABLES]);
    $[22] = getCachedData;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const getVariables = t12;
  let t13;
  if ($[24] !== getCachedData) {
    t13 = () => getCachedData([QUERY_KEYS.TRANSLATIONS]);
    $[24] = getCachedData;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const getTranslations = t13;
  let t14;
  if ($[26] !== setCacheData) {
    t14 = (dataKey_1, data_2, t152) => {
      const safeUpdate_0 = t152 === void 0 ? false : t152;
      if (!dataKey_1 || !data_2) {
        return;
      }
      setCacheData([QUERY_KEYS.DATA_KEY, dataKey_1], data_2, safeUpdate_0);
    };
    $[26] = setCacheData;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const setData = t14;
  let t15;
  if ($[28] !== setCacheData) {
    t15 = (dataKey_2, data_3, t162) => {
      const safeUpdate_1 = t162 === void 0 ? false : t162;
      if (!dataKey_2 || !data_3) {
        return;
      }
      setCacheData([QUERY_KEYS.DATA_SOURCE, dataKey_2], data_3, safeUpdate_1);
    };
    $[28] = setCacheData;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const setDataSource = t15;
  let t16;
  if ($[30] !== setCacheData) {
    t16 = (data_4, t172) => {
      const safeUpdate_2 = t172 === void 0 ? false : t172;
      if (!data_4) {
        return;
      }
      setCacheData([QUERY_KEYS.TRANSLATIONS], data_4, safeUpdate_2);
    };
    $[30] = setCacheData;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const setTranslations = t16;
  let t17;
  if ($[32] !== updateCacheData) {
    t17 = goals_0 => {
      if (!goals_0 || Object.keys(goals_0).length === 0) {
        return;
      }
      updateCacheData([QUERY_KEYS.GOALS], goals_0);
    };
    $[32] = updateCacheData;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const updateGoals = t17;
  let t18;
  if ($[34] !== updateCacheData) {
    t18 = data_5 => {
      if (!data_5 || Object.keys(data_5).length === 0) {
        return;
      }
      updateCacheData([QUERY_KEYS.VARIABLES], data_5);
    };
    $[34] = updateCacheData;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const updateVariables = t18;
  let t19;
  if ($[36] !== updateCacheData) {
    t19 = translations => {
      if (!translations || Object.keys(translations).length === 0) {
        return;
      }
      updateCacheData([QUERY_KEYS.TRANSLATIONS], translations);
    };
    $[36] = updateCacheData;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const updateTranslations = t19;
  let t20;
  if ($[38] !== resetCacheData) {
    t20 = async () => await resetCacheData([QUERY_KEYS.DATA_KEY]);
    $[38] = resetCacheData;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const resetData = t20;
  let t21;
  if ($[40] !== resetCacheData) {
    t21 = async () => await resetCacheData([QUERY_KEYS.DATA_SOURCE]);
    $[40] = resetCacheData;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  const resetDataSource = t21;
  let t22;
  if ($[42] !== resetCacheData) {
    t22 = async () => await resetCacheData([QUERY_KEYS.GOALS]);
    $[42] = resetCacheData;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const resetGoals = t22;
  let t23;
  if ($[44] !== resetCacheData) {
    t23 = async () => await resetCacheData([QUERY_KEYS.VARIABLES]);
    $[44] = resetCacheData;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  const resetVariables = t23;
  let t24;
  if ($[46] !== resetCacheData) {
    t24 = async () => await resetCacheData([QUERY_KEYS.TRANSLATIONS]);
    $[46] = resetCacheData;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const resetTranslations = t24;
  let t25;
  if ($[48] !== resetCacheData) {
    t25 = async () => {
      await resetCacheData([QUERY_KEYS.DATA_KEY]);
      await resetCacheData([QUERY_KEYS.DATA_SOURCE]);
      await resetCacheData([QUERY_KEYS.GOALS]);
      await resetCacheData([QUERY_KEYS.VARIABLES]);
      await resetCacheData([QUERY_KEYS.TRANSLATIONS]);
    };
    $[48] = resetCacheData;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  const resetDataOnly = t25;
  let t26;
  if ($[50] !== addSimulatedData || $[51] !== dataVersion || $[52] !== getCachedData || $[53] !== getData || $[54] !== getDataSource || $[55] !== getGoals || $[56] !== getTranslations || $[57] !== getVariables || $[58] !== goals || $[59] !== goalsVersion || $[60] !== resetCacheData || $[61] !== resetData || $[62] !== resetDataOnly || $[63] !== resetDataSource || $[64] !== resetGoals || $[65] !== resetTranslations || $[66] !== resetVariables || $[67] !== setCacheData || $[68] !== setData || $[69] !== setDataSource || $[70] !== setTranslations || $[71] !== updateCacheData || $[72] !== updateGoals || $[73] !== updateTranslations || $[74] !== updateVariables || $[75] !== variables || $[76] !== variablesVersion) {
    t26 = {
      setData,
      getData,
      setDataSource,
      getDataSource,
      getGoals,
      getVariables,
      setTranslations,
      setCacheData,
      getCachedData,
      updateCacheData,
      addSimulatedData,
      updateTranslations,
      updateVariables,
      updateGoals,
      getTranslations,
      resetDataOnly,
      resetData,
      resetDataSource,
      resetGoals,
      resetVariables,
      resetTranslations,
      resetCacheData,
      variablesVersion,
      goalsVersion,
      dataVersion,
      goals,
      variables
    };
    $[50] = addSimulatedData;
    $[51] = dataVersion;
    $[52] = getCachedData;
    $[53] = getData;
    $[54] = getDataSource;
    $[55] = getGoals;
    $[56] = getTranslations;
    $[57] = getVariables;
    $[58] = goals;
    $[59] = goalsVersion;
    $[60] = resetCacheData;
    $[61] = resetData;
    $[62] = resetDataOnly;
    $[63] = resetDataSource;
    $[64] = resetGoals;
    $[65] = resetTranslations;
    $[66] = resetVariables;
    $[67] = setCacheData;
    $[68] = setData;
    $[69] = setDataSource;
    $[70] = setTranslations;
    $[71] = updateCacheData;
    $[72] = updateGoals;
    $[73] = updateTranslations;
    $[74] = updateVariables;
    $[75] = variables;
    $[76] = variablesVersion;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  return t26;
};
function _temp() {
  return 0;
}
function _temp2() {
  return "null";
}
function _temp3() {}
export { useCache as default };